import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { addMonths, format, subMonths, subYears } from 'date-fns';
import * as R from 'ramda';
import { GridRowModes } from '@mui/x-data-grid-premium';
import { AuthContext } from 'authentication/AuthContext';
import { useUserRolePermissions } from 'hooks';
import { createSnack } from 'snackbar/snackbarSlice';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import {
  useEditNominationMutation,
  useElectNominationMutation,
  useGetCurrentNominationsQuery,
  useGetHistoricalNominationsQuery,
} from './nominationsEndpoints';
import Header from './Header';
import FilterTabs, { TABS } from './FilterTabs';
import MobileHeader from './MobileHeader';
import CreateNominationWizard from './create/CreateNominationWizard';
import NominationsTable from './view/NominationsTable';
import NominatedColumns from './view/NominatedColumns';
import AcceptedColumns from './view/AcceptedColumns';
import AwardedColumns from './view/AwardedColumns';
import RejectedColumns from './view/RejectedColumns';
import HistoricalColumns from './view/HistoricalColumns';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';

const periodStart = format(new Date(), 'MMMM yyyy');
const periodEnd = format(addMonths(new Date(), 2), 'MMMM yyyy');

const period = (
  <Typography variant="caption">
    Ongoing Nomination Periods: {periodStart} - {periodEnd}
  </Typography>
);

const mobileButtonStyles = {
  borderRadius: 'unset',
  flex: 1,
  fontFamily: 'Gothic A1',
  fontSize: 16,
  height: 56,
  left: 0,
  letterSpacing: 0.5,
  position: 'fixed',
  right: 0,
};

const generateHistoricalPeriods = () => {
  let months = 1;
  const goBackTo = format(subYears(new Date(), format(new Date(), 'yyyy') - 2020), 'MMMM yyyy');
  let lastPeriod = format(subMonths(new Date(), months), 'MMMM yyyy');
  const previousPeriods = [];
  while (new Date(lastPeriod) >= new Date(goBackTo)) {
    previousPeriods.push({
      from: format(new Date(lastPeriod), 'MM-dd-yyyy'),
      label: lastPeriod,
      to: format(addMonths(new Date(lastPeriod), 2), 'MM-dd-yyyy'),
    });
    lastPeriod = format(subMonths(new Date(), (months += 1)), 'MMMM yyyy');
  }

  return previousPeriods;
};

const Nomination = () => {
  const { view } = useParams();
  const periods = generateHistoricalPeriods();
  const [tabFilter, setTabFilter] = useState(view?.toUpperCase() || TABS.NOMINATED);
  const [rowModesModel, setRowModesModel] = useState({});
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isUnsavedOpen, setIsUnsavedOpen] = useState(false);
  const [currentNomId, setCurrentNomId] = useState();
  const [nominations, setNominations] = useState({});
  const [copiedNomination, setCopiedNomination] = useState(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [historicalPeriod, setHistoricalPeriod] = useState(periods[0]);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    let isValidTab;
    if (view === 'new') {
      setTabFilter(TABS.NOMINATED);
      setIsCreateDrawerOpen(true);
    } else {
      isValidTab = [TABS.AWARDED, TABS.ACCEPTED, TABS.REJECTED, TABS.HISTORICAL].includes(
        view?.toUpperCase()
      );
      if (!isValidTab) {
        setTabFilter(TABS.NOMINATED);
        navigate('../monthly-nominations/nominated', { replace: true });
      }
      setTabFilter(view.toUpperCase());
    }
  }, [view, navigate]);

  const { isProductShippingManage, isProductShippingView } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);

  const isProductShippingViewOnly = R.and(isProductShippingView, R.not(isProductShippingManage));

  const hasCreatePermissions = R.and(
    isProductShippingManage,
    affiliates.some(R.propEq('shipper', true))
  );

  const handleTabChange = R.compose(
    navigate,
    R.concat('../monthly-nominations/'),
    R.toLower,
    R.nthArg(1)
  );

  const { data: nominatedQuery = [], isFetching: isFetchingNominated } =
    useGetCurrentNominationsQuery({
      nominationState: TABS.NOMINATED,
    });

  const { data: awardedQuery = [], isFetching: isFetchingAwarded } = useGetCurrentNominationsQuery({
    nominationState: TABS.AWARDED,
  });

  const { data: acceptedQuery = [], isFetching: isFetchingAccepted } =
    useGetCurrentNominationsQuery({
      nominationState: TABS.ACCEPTED,
    });

  const { data: rejectedQuery = [], isFetching: isFetchingRejected } =
    useGetCurrentNominationsQuery({
      nominationState: TABS.REJECTED,
    });

  const { data: historicalQuery = [], isFetching: isFetchingHistorical } =
    useGetHistoricalNominationsQuery({
      fromDate: historicalPeriod.from,
      toDate: historicalPeriod.to,
    });

  const tabs = [
    {
      count: nominatedQuery.length,
      id: TABS.NOMINATED,
      name: 'Nominated',
    },
    {
      count: awardedQuery.length,
      id: TABS.AWARDED,
      name: 'Awarded',
    },
    {
      count: acceptedQuery.length,
      id: TABS.ACCEPTED,
      name: 'Accepted',
    },
    {
      count: rejectedQuery.length,
      id: TABS.REJECTED,
      name: 'Rejected',
    },
    {
      count: historicalQuery.length,
      id: TABS.HISTORICAL,
      name: 'Historical',
    },
  ];

  const isLoading = [
    isFetchingNominated,
    isFetchingAwarded,
    isFetchingAccepted,
    isFetchingRejected,
    isFetchingHistorical,
  ].includes(true);

  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const isAwardedTab = view.toUpperCase() === TABS.AWARDED;
  const [submitVolume] = isAwardedTab ? useElectNominationMutation() : useEditNominationMutation();

  const submitNomination = nomination => {
    const { nominationId } = nomination;
    submitVolume(nomination)
      .unwrap()
      .then(data => {
        dispatch(
          createSnack({
            message: data.message,
          })
        );
      })
      .catch(error => {
        dispatch(
          createSnack({
            message: error.data.title,
            options: { variant: 'error' },
          })
        );
      });
    setRowModesModel({ ...rowModesModel, [nominationId]: { mode: GridRowModes.View } });
    setIsRejectOpen(false);
  };

  const handleCancel = id => () => {
    if (!nominations[id].hasChanged || !nominations[id].isValid) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { ignoreModifications: true, mode: GridRowModes.View },
      });
    } else {
      setCurrentNomId(id);
      setIsUnsavedOpen(true);
    }
  };

  const handleLeave = id => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { ignoreModifications: true, mode: GridRowModes.View },
    });
    setIsUnsavedOpen(false);
  };

  const handleCopyNomination = id => {
    const c = [nominatedQuery, awardedQuery, acceptedQuery, historicalQuery]
      .flat()
      .find(n => n.nominationId === id);
    setCopiedNomination(c);
    setIsCreateDrawerOpen(true);
  };

  const nominationsTableProps = R.cond([
    [
      R.equals(TABS.AWARDED),
      R.always({
        columns: AwardedColumns({
          handleCancel,
          handleCopyNomination,
          isProductShippingViewOnly,
          nominations,
          rowModesModel,
          setCurrentNomId,
          setIsRejectOpen,
          setNominations,
          setRowModesModel,
          submitNomination,
        }),
        isFetching: isFetchingAwarded,
        nominationLabel: 'Awarded',
        queryData: awardedQuery,
      }),
    ],
    [
      R.equals(TABS.ACCEPTED),
      R.always({
        columns: AcceptedColumns({ handleCopyNomination, isProductShippingViewOnly }),
        handleCopyNomination,
        isFetching: isFetchingAccepted,
        nominationLabel: 'Accepted',
        queryData: acceptedQuery,
      }),
    ],
    [
      R.equals(TABS.REJECTED),
      R.always({
        columns: RejectedColumns,
        isFetching: isFetchingRejected,
        nominationLabel: 'Rejected',
        queryData: rejectedQuery,
      }),
    ],
    [
      R.either(R.equals(TABS.NOMINATED), R.equals('NEW')),
      R.always({
        columns: NominatedColumns({
          handleCancel,
          handleCopyNomination,
          isProductShippingViewOnly,
          nominations,
          rowModesModel,
          setNominations,
          setRowModesModel,
          submitNomination,
        }),
        isFetching: isFetchingNominated,
        nominationLabel: 'Nominated',
        queryData: nominatedQuery,
      }),
    ],
    [
      R.equals(TABS.HISTORICAL),
      R.always({
        columns: HistoricalColumns({ handleCopyNomination, isProductShippingViewOnly }),
        handleCopyNomination,
        historicalPeriod,
        isFetching: isFetchingHistorical,
        nominationLabel: 'Historical',
        periods,
        queryData: historicalQuery,
        setHistoricalPeriod,
      }),
    ],
  ])(tabFilter);

  const addNewIcon = <AddRoundedIcon sx={{ mt: '-2px' }} />;
  const buttonStyles = lgUp
    ? {
        ml: 2,
      }
    : mobileButtonStyles;

  const handleCreate = () => setIsCreateDrawerOpen(true);

  const buttonLabel = lgUp ? 'New Nomination' : 'Add New';
  const button = hasCreatePermissions && (
    <Button onClick={handleCreate} startIcon={addNewIcon} sx={buttonStyles}>
      {buttonLabel}
    </Button>
  );

  const filterTabs = tabFilter && (
    <FilterTabs
      activeTab={tabFilter}
      isLoading={isLoading}
      onChange={handleTabChange}
      tabs={tabs}
    />
  );

  const mobileHeader = hasCreatePermissions && <MobileHeader button={button} />;
  return (
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    <Box sx={{ pb: 0, pt: { xs: 7, lg: 10 }, px: { xs: 0, lg: 3 } }}>
      {lgUp ? <Header button={button} period={period} tabs={filterTabs} /> : mobileHeader}
      {!R.isNil(tabFilter) && (
        <Stack spacing={2} sx={{ p: 1, pb: 0 }}>
          {!lgUp && period}
          <NominationsTable
            {...nominationsTableProps}
            rowModesModel={rowModesModel}
            setRowModesModel={setRowModesModel}
          />
          <CreateNominationWizard
            copiedNomination={copiedNomination}
            isDrawerOpen={isCreateDrawerOpen}
            setCopiedNomination={setCopiedNomination}
            setIsDrawerOpen={setIsCreateDrawerOpen}
          />
          {isRejectOpen && (
            <Dialog
              maxWidth="xs"
              open
              slotProps={{
                paper: { elevation: 10 },
              }}
              sx={{ '& .MuiDialog-paper': { width: 330 } }}
            >
              <DialogTitle>Reject Nomination?</DialogTitle>
              <DialogContent>
                <Typography pb={2}>
                  You are about to reject your nomination with an awarded volume of{' '}
                  {formatNumberWithoutDecimals(nominations[currentNomId].volAwarded)} bbl.
                </Typography>
                <Typography>Are you sure you want to continue?</Typography>
              </DialogContent>
              <DialogActions sx={{ pb: 2, px: 2 }}>
                <Button
                  autoFocus
                  onClick={() => {
                    submitNomination(nominations[currentNomId]);
                  }}
                  variant="outlined"
                >
                  Continue
                </Button>
                <Button onClick={() => setIsRejectOpen(false)}>Keep Editing</Button>
              </DialogActions>
            </Dialog>
          )}
          {isUnsavedOpen && (
            <UnsavedChangesDialog
              handleContinue={() => handleLeave(currentNomId)}
              handleKeepEditing={() => setIsUnsavedOpen(false)}
              isOpen
            />
          )}
        </Stack>
      )}
      {!lgUp && filterTabs}
    </Box>
  );
};

export default Nomination;
